import { useLocation, useNavigationType, useOutlet } from 'react-router';
import { motion, MotionConfig } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { OnboardingBack } from '@/components/onboarding/onboarding-chrome';
import { MotionLink } from '@/components/motion/motion-ui';
import { StepStage } from '@/components/motion/step-stage';
import { enterTransition, pressSpring, SCREEN_SLIDE } from '@/components/motion/tokens';
import { pressProps } from '@/components/motion/variants';
import { OnboardingBackdrop } from '@/components/onboarding/onboarding-backdrop';
import { OnboardingBackProvider, useOnboardingBackChannel } from '@/components/onboarding/onboarding-chrome';
import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { useStrings } from '@/i18n/use-strings';

/**
 * The onboarding chrome, mounted **once** for the whole flow: the brand
 * photograph and its scrim, the fixed header row, and the frosted card. `/`,
 * `/start` and `/h/:theme/profile` render only what goes *inside* the card,
 * through the outlet below.
 *
 * This is a pathless layout route (`layout()` in `routes.ts`), so it adds no
 * URL segment and changes no history entry — the three screens keep their own
 * URLs, loaders and guards, and back/forward behave exactly as before. What it
 * changes is the DOM: because all three are children of one route, React
 * Router keeps this element tree mounted across them, so the `<img>`, its
 * inlined thumbnail, the scrim and the `backdrop-filter` layer are the same
 * nodes on every step. That is the whole point of the pass — the photograph
 * used to be re-mounted per screen, which re-ran its decode-gated reveal and
 * re-created the blur layer, and the flow flashed between steps.
 *
 * Two things follow from it:
 *
 * - onboarding navigations deliberately do **not** use React Router's
 *   `viewTransition`. A root view transition cross-fades a snapshot of the
 *   whole document, photograph included, which is exactly the flash this pass
 *   removes; the persistent card plus `motion` now owns the transition, and it
 *   moves only the content. The handoff *out* of onboarding to the journey
 *   still uses one, because there the whole screen genuinely changes.
 * - the card never animates its own box. A `backdrop-filter` surface that
 *   animates size re-blurs every frame, so the arriving content is what moves
 *   (see `StepStage`) and the card simply takes its height.
 *
 * Everything about the chrome except the back affordance is decided by the
 * path, which keeps it correct on a server render: the welcome hero is the only
 * screen that is navy glass, centred on desktop and without a header logo. The
 * back affordance is published by the screen itself, because `/start`'s second
 * question means "back" to be a step rather than a URL.
 */
export default function OnboardingLayout() {
  const { pathname } = useLocation();
  const { direction, siteSettings } = useRootLoaderData();
  const { content } = useStrings();

  const outlet = useOutlet();
  const { back: published, publish } = useOnboardingBackChannel();

  const isWelcome = pathname === '/';

  // A screen publishes its own back target from an effect, which does not run
  // on the server — so the row falls back to the one target that is right for
  // every question screen on a first paint: the step before it. The screens
  // then confirm or replace it before the browser paints again.
  const fallbackBack: OnboardingBack = { to: '/', label: content.content_back };
  const back = published ?? (isWelcome ? null : fallbackBack);

  // `POP` covers both the browser's back button and a screen's own back
  // affordance, so the content travels back the way it came.
  const movement = useNavigationType() === 'POP' ? 'backward' : 'forward';

  const backClassName =
    'bp-tappable -ms-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-sm font-semibold text-white/90 transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none motion-reduce:transition-none';

  const backContent = back ? (
    <>
      <Icon name="passport/chevron-left" className="size-4 rtl:rotate-180" />
      {back.label}
    </>
  ) : null;

  return (
    <MotionConfig reducedMotion="user" transition={enterTransition}>
      <main className="relative isolate flex flex-1 flex-col">
        {/* One photograph for the whole flow: `home_background`, the
            lifesavers. Switching grounds mid-flow read as three unrelated
            pages, and now it would also cost the flow its persistent stage.
            The journey and the reward screens stand on the *other* ground,
            `secondary_background` — see `JourneyGround`. */}
        <OnboardingBackdrop src={siteSettings.homeBackgroundUrl} />

        {/* `viewport-fit=cover` lets the photograph run under the status bar;
            the row pads itself back out so the controls never do.

            The row is a fixed 4rem whether or not it carries a back link or a
            logo, so the card below it starts at the same y on all three screens
            and neither a slow logo nor a step change nudges it. */}
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 px-5 pt-[env(safe-area-inset-top)] sm:px-8">
          {back ? (
            back.to === undefined ? (
              <motion.button
                type="button"
                onClick={back.onClick}
                {...pressProps}
                transition={pressSpring}
                className={backClassName}
              >
                {backContent}
              </motion.button>
            ) : (
              <MotionLink to={back.to} {...pressProps} transition={pressSpring} className={backClassName}>
                {backContent}
              </MotionLink>
            )
          ) : (
            <span />
          )}

          {/*
            Reserved box, `object-contain` inside it: `site_settings` can point
            this at any Prismic image, so the intrinsic ratio is unknowable at
            build time and only a fixed box can guarantee no shift on load. The
            welcome screen has no header logo — its hero wordmark is the brand.
          */}
          {isWelcome ? (
            <span className="h-12 w-52" />
          ) : (
            <MotionLink
              to="/"
              {...pressProps}
              transition={pressSpring}
              className="bp-tappable inline-flex h-12 w-52 items-center justify-end"
              aria-label={siteSettings.seo.defaultTitle}
            >
              {/* The box is 48 px tall for the target and 208 px wide for the
                  roughly 4:1 SLSA lockup. 208 px is what the row has left
                  beside Back.

                  Through `Media`, with the bundled lockup's own dimensions and
                  the same treatment `AppHeader` gives it: intrinsic size on the
                  element, decoded eagerly, and a one-shot fade over nothing —
                  a tinted placeholder would show as a plate on the photograph. */}
              {/* `header_logo_height` overrides the height within the same box. */}
              <Media
                src={siteSettings.onboardingLogoUrl}
                alt=""
                width={818}
                height={204}
                priority
                placeholder="transparent"
                className="max-h-12 max-w-full object-contain"
                style={
                  siteSettings.logoHeights.header === null ? undefined : { height: siteSettings.logoHeights.header }
                }
              />
            </MotionLink>
          )}
        </header>

        {/*
          Thumb zone. On a phone the card is anchored to the *bottom* of the
          viewport — the choices and the primary action sit where a thumb
          already is, and the lifesavers get the room above them — and it pads
          itself past the home indicator. From `sm` up there is no thumb to
          reach with, so the composition returns to centred (welcome) or
          top-aligned (a question).
        */}
        <div
          className={cn(
            'mx-auto flex w-full max-w-lg flex-1 flex-col justify-end px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-8 sm:pb-8',
            isWelcome ? 'sm:justify-center sm:py-6' : 'sm:justify-start sm:pt-4',
          )}
        >
          {/*
            The step card, in one of the flow's two materials — and one DOM
            node for both, so the blur layer is created once and sampled once.

            - `glass` — navy frost, white ink. The welcome hero, where the pitch
              is short and the lifesavers should read *through* it.
            - `frost` — white frost, navy ink. The question screens: body copy,
              labels and a text input all need a light surface.

            Both blurs are static. Nothing transitions or animates
            `backdrop-filter`; only the surface colours cross over when the
            material changes, once, on the way out of the welcome screen.
          */}
          <section
            className={cn(
              'bp-surface rounded-[1.75rem]',
              isWelcome ? 'bp-glass p-5 text-white sm:p-8' : 'bp-card text-bp-ink p-6 sm:p-8',
            )}
          >
            <OnboardingBackProvider value={publish}>
              <StepStage stepKey={pathname} direction={direction} movement={movement} travel={SCREEN_SLIDE}>
                {outlet}
              </StepStage>
            </OnboardingBackProvider>
          </section>
        </div>
      </main>
    </MotionConfig>
  );
}
